import type { OnboardingStep, OnboardingTour } from '../types';
import { getDashboardRevenueValues, getDashboardSpendValues } from '../dashboardValues';
import { hasMultipleRestaurants, prepareRestaurantSwitch, getRequestedRestaurantValues, requestedRestaurantSelected, getSelectedRestaurantValues } from '../tourRestaurant';
import { useFiltersStore } from '../../../stores/appFiltersStore';
import { calculateEndDate } from '../../../utils/dateUtils';
import { useTimeIntervalStore } from '../../../stores/appTimeIntervalStore';

const skip = 'onboarding.skip';
const next = 'onboarding.next';

export const dashboardSteps: OnboardingStep[] = [
  {
    id: 'welcome',
    onEnter: () => useFiltersStore.getState().setOverallTypeFilter('all'),
    route: '/dashboard',
    layout: 'hero',
    illustration: 'welcome',
    titleKey: 'onboarding.dashboard.welcome.title',
    descriptionKey: 'onboarding.dashboard.welcome.description',
    primaryLabelKey: 'onboarding.showMeAround',
    secondaryLabelKey: skip,
  },
  {
    id: 'revenue',
    route: '/dashboard',
    target: 'kpi-revenue',
    layout: 'spotlight',
    illustration: 'revenue',
    titleKey: 'onboarding.dashboard.revenue.title',
    descriptionKey: 'onboarding.dashboard.revenue.description',
    values: getDashboardRevenueValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'below',
  },
  {
    id: 'spend',
    route: '/dashboard',
    target: 'kpi-spend',
    layout: 'spotlight',
    illustration: 'spend',
    titleKey: 'onboarding.dashboard.spend.title',
    descriptionKey: 'onboarding.dashboard.spend.description',
    values: getDashboardSpendValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'below',
  },
  {
    id: 'cost',
    route: '/dashboard',
    target: 'kpi-cost',
    layout: 'spotlight',
    illustration: 'cost',
    titleKey: 'onboarding.dashboard.cost.title',
    descriptionKey: 'onboarding.dashboard.cost.description',
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'below',
  },
  {
    id: 'restaurant',
    isAvailable: hasMultipleRestaurants,
    route: '/dashboard',
    target: 'restaurant-selector',
    layout: 'compact',
    illustration: 'restaurant',
    titleKey: 'onboarding.dashboard.restaurant.title',
    descriptionKey: 'onboarding.dashboard.restaurant.description',
    onEnter: prepareRestaurantSwitch,
    values: getRequestedRestaurantValues,
    advanceWhen: requestedRestaurantSelected,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    allowTargetInteraction: true,
    highlightTarget: true,
    showArrow: true,
    placement: 'below',
    padding: 10,
    radius: 14,
  },
  {
    id: 'restaurant-selected',
    isAvailable: hasMultipleRestaurants,
    route: '/dashboard',
    layout: 'compact',
    illustration: 'restaurant',
    titleKey: 'onboarding.dashboard.restaurantSelected.title',
    descriptionKey: 'onboarding.dashboard.restaurantSelected.description',
    values: getSelectedRestaurantValues,
    primaryLabelKey: next,
  },
  ...(['food', 'drinks', 'all'] as const).flatMap((category): OnboardingStep[] => [
    {
      id: `category-${category}`, route: '/dashboard', target: `overall-type-${category}`,
      layout: 'compact', illustration: 'category',
      titleKey: `onboarding.dashboard.categories.${category}.title`,
      descriptionKey: `onboarding.dashboard.categories.${category}.description`,
      primaryLabelKey: next, secondaryLabelKey: skip,
      allowTargetInteraction: true, highlightTarget: true, showArrow: true,
      advanceWhen: () => useFiltersStore.getState().overallTypeFilter === category,
      padding: 6, radius: 14,
    },
    ...(category === 'all' ? [] : [{
      onEnter: () => useFiltersStore.getState().setOverallTypeFilter(category),
      id: `category-${category}-selected`, route: '/dashboard', target: 'page-kpis',
      layout: 'compact' as const,
      titleKey: `onboarding.dashboard.categories.${category}Selected.title`,
      descriptionKey: `onboarding.dashboard.categories.${category}Selected.description`,
      primaryLabelKey: next,
    }]),
  ]),
  {
    id: 'time', route: '/dashboard', target: 'time-controls', layout: 'compact', illustration: 'time',
    titleKey: 'onboarding.dashboard.time.title', descriptionKey: 'onboarding.dashboard.time.description',
    primaryLabelKey: next, secondaryLabelKey: skip, showArrow: true,
    onEnter: () => {
      const time = useTimeIntervalStore.getState();
      time.setCustomStartDate(null);
      time.setCustomEndDate(null);
      time.setView('week');
    },
  },
  {
    id: 'time-month', route: '/dashboard', target: 'time-controls-month', layout: 'compact',
    titleKey: 'onboarding.dashboard.month.title', descriptionKey: 'onboarding.dashboard.month.description',
    primaryLabelKey: next, secondaryLabelKey: skip,
    allowTargetInteraction: true, highlightTarget: true, showArrow: true,
    advanceWhen: () => useTimeIntervalStore.getState().view === 'month',
  },
  {
    id: 'time-previous', route: '/dashboard', target: 'time-previous', layout: 'compact',
    titleKey: 'onboarding.dashboard.previousMonth.title', descriptionKey: 'onboarding.dashboard.previousMonth.description',
    primaryLabelKey: next, secondaryLabelKey: skip,
    onEnter: () => useTimeIntervalStore.getState().setView('month'),
    allowTargetInteraction: true, highlightTarget: true, showArrow: true,
    advanceWhen: () => {
      const { view, startDate } = useTimeIntervalStore.getState();
      const now = new Date();
      const previous = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      return view === 'month' && startDate.getFullYear() === previous.getFullYear()
        && startDate.getMonth() === previous.getMonth();
    },
  },
  {
    onEnter: () => {
      const time = useTimeIntervalStore.getState();
      const now = new Date();
      const previous = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      time.setView('month');
      time.setDateRange(previous, calculateEndDate(previous, 'month'));
    },
    id: 'time-result', route: '/dashboard', target: 'page-kpis', layout: 'compact',
    titleKey: 'onboarding.dashboard.lastMonth.title', descriptionKey: 'onboarding.dashboard.lastMonth.description',
    primaryLabelKey: next,
  },
];

export const dashboardCompleteStep: OnboardingStep = {
  id: 'complete',
  route: '/dashboard',
  layout: 'complete',
  illustration: 'complete',
  titleKey: 'onboarding.dashboard.complete.title',
  descriptionKey: 'onboarding.dashboard.complete.description',
  primaryLabelKey: 'onboarding.viewRevenue',
  primaryRoute: '/revenuehistory',
  primaryTour: 'revenue',
  secondaryLabelKey: 'onboarding.explorePage',
  secondaryAction: 'complete',
};

export const dashboardTour: OnboardingTour = {
  id: 'dashboard',
  paths: ['/dashboard'],
  steps: [...dashboardSteps, dashboardCompleteStep],
};
